/**
 * Listing: an abstract, custom-field-driven "featured item" (a shop job, a vehicle,
 * a property). The shape of `fields` is defined by a `ListingSchema` that comes from
 * site configuration, never from this package, so the same model serves every vertical.
 *
 * Persisted as plain JSON (`ListingData`) inside site content. `toData()` also emits
 * the legacy `imageUrl` so older readers of `portfolioItems` keep working (additive).
 */
export class ListingSchema {
    fields;
    constructor(fields = []) {
        const seen = new Set();
        const clean = [];
        for (const f of fields) {
            const key = f.key.trim();
            if (!key || seen.has(key))
                continue;
            seen.add(key);
            clean.push({ ...f, key, kind: f.kind ?? "text" });
        }
        this.fields = clean;
    }
    def(key) {
        return this.fields.find((f) => f.key === key);
    }
    cardFields() {
        return this.fields.filter((f) => f.onCard);
    }
    /** Normalizes raw input for a field. Number fields keep digits and separators only. */
    coerce(key, raw) {
        const def = this.def(key);
        if (def?.kind === "number")
            return raw.replace(/[^0-9.,-]/g, "").trim();
        return raw;
    }
    /** Display string for a stored value, with the field's prefix/suffix; "" when empty. */
    display(key, value) {
        const v = (value ?? "").trim();
        if (!v)
            return "";
        const def = this.def(key);
        return `${def?.prefix ?? ""}${v}${def?.suffix ? ` ${def.suffix.trim()}` : ""}`;
    }
}
export class Listing {
    id;
    title;
    summary;
    priceLabel;
    images;
    description;
    fields;
    featured;
    archived;
    constructor(p) {
        this.id = p.id;
        this.title = p.title;
        this.summary = p.summary;
        this.priceLabel = p.priceLabel;
        this.images = p.images;
        this.description = p.description;
        this.fields = p.fields;
        this.featured = p.featured;
        this.archived = p.archived;
    }
    /** `fallbackId` is used for legacy rows that were saved without an id. */
    static fromData(data, fallbackId) {
        const images = Array.isArray(data.images)
            ? data.images.filter((u) => typeof u === "string" && u.trim() !== "")
            : data.imageUrl
                ? [data.imageUrl]
                : [];
        const fields = {};
        for (const [k, v] of Object.entries(data.fields ?? {})) {
            if (typeof v === "string")
                fields[k] = v;
        }
        return new Listing({
            id: data.id?.trim() || fallbackId,
            title: data.title ?? "",
            summary: data.summary ?? "",
            priceLabel: data.priceLabel ?? "",
            images,
            description: data.description ?? "",
            fields,
            featured: data.featured !== false,
            archived: data.archived === true,
        });
    }
    /** New empty listing (admin "create listing"). Starts pinned off so it never surprises the home page. */
    static blank(id) {
        return new Listing({
            id,
            title: "",
            summary: "",
            priceLabel: "",
            images: [],
            description: "",
            fields: {},
            featured: false,
            archived: false,
        });
    }
    get coverImage() {
        return this.images[0];
    }
    with(patch) {
        return new Listing({ ...this.toProps(), ...patch });
    }
    toProps() {
        return {
            id: this.id,
            title: this.title,
            summary: this.summary,
            priceLabel: this.priceLabel,
            images: this.images,
            description: this.description,
            fields: this.fields,
            featured: this.featured,
            archived: this.archived,
        };
    }
    withCore(patch) {
        return this.with(patch);
    }
    withField(key, value) {
        return this.with({ fields: { ...this.fields, [key]: value } });
    }
    withFeatured(featured) {
        return this.with({ featured });
    }
    withArchived(archived) {
        return this.with({ archived });
    }
    withImages(images) {
        return this.with({ images: [...images] });
    }
    appendImages(urls) {
        return this.withImages([...this.images, ...urls]);
    }
    removeImage(index) {
        if (index < 0 || index >= this.images.length)
            return this;
        return this.withImages(this.images.filter((_, i) => i !== index));
    }
    /** Swaps an image with its neighbour; out-of-range moves are no-ops. */
    moveImage(index, direction) {
        const target = direction === "left" ? index - 1 : index + 1;
        if (index < 0 || index >= this.images.length || target < 0 || target >= this.images.length) {
            return this;
        }
        const next = [...this.images];
        const a = next[index];
        next[index] = next[target];
        next[target] = a;
        return this.withImages(next);
    }
    /** Case-insensitive match over title, summary, price and every custom field value. */
    matches(query) {
        const q = query.trim().toLowerCase();
        if (!q)
            return true;
        const hay = [this.title, this.summary, this.priceLabel, ...Object.values(this.fields)]
            .join(" ")
            .toLowerCase();
        return hay.includes(q);
    }
    toData() {
        const data = {
            id: this.id,
            title: this.title,
            summary: this.summary,
            featured: this.featured,
        };
        if (this.priceLabel)
            data.priceLabel = this.priceLabel;
        if (this.images.length > 0) {
            data.images = [...this.images];
            data.imageUrl = this.images[0];
        }
        if (this.description)
            data.description = this.description;
        if (Object.keys(this.fields).length > 0)
            data.fields = { ...this.fields };
        if (this.archived)
            data.archived = true;
        return data;
    }
}
//# sourceMappingURL=Listing.js.map